import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../i18n/LanguageContext';
import useShippingRequest from '../../hooks/useShippingRequest';
import distributorService from '../../services/distributorService';
import { formatDeliveryDate } from '../../utils/orderDates';
import { validEntries } from '../distributor-cash/CashBank';
import { usd } from '../distributor-cash/cashMoney';
import { distributorHoldLabel } from '../../features/orders/DistributorOrderDetails';
import { upcomingDeliveries } from './overviewActivity';

const labels = {
  deposit: 'Funds added',
  order_debit: 'Order charge',
  order_reversal: 'Order reversal',
};

export default function DistributorOverviewActivity({ t, refresh, onOpenOrder, opening }) {
  const locale = useLocale();
  const { begin, cancel, version } = useShippingRequest('overview-upcoming-and-ledger');
  const [deliveries, setDeliveries] = useState(null);
  const [history, setHistory] = useState(null);
  const [deliveryError, setDeliveryError] = useState(false);
  const [bankError, setBankError] = useState(false);
  useEffect(() => {
    const request = begin();
    setDeliveries(null);
    setHistory(null);
    setDeliveryError(false);
    setBankError(false);
    upcomingDeliveries(request.signal)
      .then((rows) => {
        if (request.isCurrent()) setDeliveries(rows);
      })
      .catch(() => {
        if (request.isCurrent()) setDeliveryError(true);
      });
    Promise.resolve(distributorService.cashEntries(undefined, 1, { signal: request.signal }))
      .then((response) => {
        const value = response?.data?.data;
        if (
          !validEntries(value, 1) ||
          new Set(value.rows.map((row) => row.id)).size !== value.rows.length ||
          new Set(value.rows.map((row) => row.sequence)).size !== value.rows.length
        )
          throw new Error('Invalid bank activity');
        if (request.isCurrent())
          setHistory([...value.rows].sort((a, b) => b.sequence - a.sequence).slice(0, 3));
      })
      .catch(() => {
        if (request.isCurrent()) setBankError(true);
      });
    return cancel;
  }, [begin, cancel, version, refresh]);
  return (
    <div className="me-overview-secondary">
      <section className="me-overview-panel" aria-label={t('Upcoming deliveries')}>
        <header className="me-overview-panel-heading">
          <h2>{t('Upcoming deliveries')}</h2>
          <Link to="/distributor/shipments" className="me-dw-link">
            {t('View shipments')} <span aria-hidden="true">→</span>
          </Link>
        </header>
        {deliveryError ? (
          <p className="me-overview-empty" role="status">
            {t('Deliveries are unavailable. Refresh to try again.')}
          </p>
        ) : !deliveries ? (
          <p className="me-overview-empty" role="status">
            {t('Loading deliveries…')}
          </p>
        ) : !deliveries.length ? (
          <p className="me-overview-empty">{t('No upcoming delivery dates.')}</p>
        ) : (
          <ul className="me-overview-activity-list">
            {deliveries.map((row) => (
              <li key={row._id}>
                <time
                  dateTime={row.deliveryDate.slice(0, 10)}
                  className="me-overview-delivery-date"
                >
                  {formatDeliveryDate(row.deliveryDate, locale)}
                </time>
                <div className="me-overview-activity-copy">
                  <button
                    type="button"
                    className="me-dw-link"
                    onClick={() => onOpenOrder(row._id)}
                    disabled={!!opening}
                    aria-label={t('View order {id}', { id: row.orderId })}
                  >
                    {row.orderId}
                  </button>
                  <span>{row.clinic?.name || '—'}</span>
                  {distributorHoldLabel(row) && (
                    <small className="me-overview-warning">{t(distributorHoldLabel(row))}</small>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="me-overview-panel" aria-label={t('Recent bank activity')}>
        <header className="me-overview-panel-heading">
          <h2>{t('Recent bank activity')}</h2>
          <Link to="/distributor/bank" className="me-dw-link">
            {t('View bank activity')} <span aria-hidden="true">→</span>
          </Link>
        </header>
        {bankError ? (
          <p className="me-overview-empty" role="status">
            {t('Bank activity is unavailable. Refresh to try again.')}
          </p>
        ) : !history ? (
          <p className="me-overview-empty" role="status">
            {t('Loading bank activity…')}
          </p>
        ) : !history.length ? (
          <p className="me-overview-empty">{t('No cash activity yet.')}</p>
        ) : (
          <ul className="me-overview-activity-list">
            {history.map((row) => (
              <li key={row.id}>
                <div className="me-overview-activity-copy">
                  <strong>{t(labels[row.kind])}</strong>
                  <span>
                    {row.orderNumber && `${row.orderNumber} · `}
                    <time dateTime={row.createdAt}>
                      {new Date(row.createdAt).toLocaleDateString(locale, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </time>
                  </span>
                </div>
                <strong
                  className={`me-overview-activity-amount${row.deltaCents > 0 ? ' is-credit' : ''}`}
                >
                  {row.deltaCents > 0 ? '+' : ''}
                  {usd(row.deltaCents)}
                </strong>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
